import { Applicant, ApplicantDoc, ApplicantsDict } from "../types/applicants";
import { AppUser, AuthUser } from "../types/users";
import {
  CollectionReference,
  Query,
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import db, { BASE_FUNCTIONS_URL } from "../types/firebase";

const CODE_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const CODE_LENGTH = 8;
const MAX_GENERATION_ATTEMPTS = 25;

type ExistingApplicantMatch = {
  id: string;
  email: string;
};

const normalizeApplicantSponsorIds = (applicant: Applicant): Applicant => {
  const sponsorIds = Array.from(
    new Set(
      (applicant.sponsorIds || []).filter((sponsorId): sponsorId is string => !!sponsorId),
    ),
  );

  if (sponsorIds.length > 0) {
    return {
      ...applicant,
      sponsorIds,
    };
  }

  if (applicant.sponsorId) {
    return {
      ...applicant,
      sponsorIds: [applicant.sponsorId],
    };
  }

  return {
    ...applicant,
    sponsorIds: [],
  };
};

const createRandomEmailExtensionCode = (length: number = CODE_LENGTH) => {
  let result = "";

  for (let index = 0; index < length; index += 1) {
    const randomIndex = Math.floor(Math.random() * CODE_ALPHABET.length);
    result += CODE_ALPHABET[randomIndex];
  }

  return result;
};

const isEmailExtensionCodeInUse = async (code: string, applicantId: string) => {
  const snapshot = await getDocs(
    query(collection(db, "applicants"), where("email_extension_code", "==", code), limit(1))
  );

  if (snapshot.empty) {
    return false;
  }

  return snapshot.docs[0].id !== applicantId;
};

export const existingApplicants = async (
  _user: AppUser | null | undefined,
  emails: string[],
): Promise<ExistingApplicantMatch[]> => {
  const normalizedEmails = emails
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

  if (!normalizedEmails.length) {
    return [];
  }

  const duplicates: ExistingApplicantMatch[] = [];

  // Function to chunk the array
  const chunkArray = (myArray: string[], chunk_size: number) => {
    let index = 0;
    let arrayLength = myArray.length;
    let tempArray = [];

    for (index = 0; index < arrayLength; index += chunk_size) {
      let chunk = myArray.slice(index, index + chunk_size);
      tempArray.push(chunk);
    }

    return tempArray;
  };

  // Chunk the emails array into subarrays of 25
  const emailChunks = chunkArray(normalizedEmails, 25);

  for (const emailChunk of emailChunks) {
    console.log("querying for emails: ", emailChunk);
    const q = query(collection(db, "applicants"), where("email", "in", emailChunk));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      duplicates.push({ id: doc.id, email: doc.data().email.toLowerCase() });
    });
  }

  console.log("found duplicates: ", duplicates);

  return duplicates;
};

export const loadApplicant = async (applicantId: string) => {
  let loadedApplicant = null;

  if (applicantId) {
    const docRef = doc(db, "applicants", applicantId);
    const applicantSnapshot = await getDoc(docRef);
    if (applicantSnapshot) {
      loadedApplicant = {
        id: applicantSnapshot.id,
        applicant: normalizeApplicantSponsorIds(applicantSnapshot.data() as Applicant),
      };
    }
  }
  return loadedApplicant;
};

export const loadApplicants = async (
  user: AppUser | null | undefined,
  filter: Record<string, string> = {},
  cLimit: number = 0
) => {
  let applicantsRef: Query | CollectionReference;
  const sponsorId = user && user.sponsorId ? user.sponsorId : "";
  //  const agentId = user && user.agentId ? user.agentId : "";

  // if (agentId !== "") {
  //   applicantsRef = query(collection(db, "applicants"), where("agentId", "==", agentId));
  // } else
  if (sponsorId !== "") {
    applicantsRef = query(
      collection(db, "applicants"),
      where("sponsorIds", "array-contains", sponsorId),
    );
  } else {
    applicantsRef = collection(db, "applicants");
  }

  Object.keys(filter).forEach((key) => {
    applicantsRef = query(applicantsRef, where(key, "==", filter[key]));
  });
  if (cLimit > 0) applicantsRef = query(applicantsRef, limit(cLimit));

  const applicantsSnapshot = await getDocs(applicantsRef);
  const applicantsDict: ApplicantsDict = {};

  applicantsSnapshot.forEach((doc) => {
    const applicant = normalizeApplicantSponsorIds(doc.data() as Applicant);
    applicantsDict[doc.id] = applicant;
  });

  return applicantsDict;
};

export const storeApplicant = async (
  user: AppUser | null | undefined,
  applicantDoc: ApplicantDoc | null,
  addIfNew: boolean
) => {
  if (!(applicantDoc && applicantDoc.applicant)) {
    return null;
  }

  let applicantSnapshot = null;
  applicantDoc.applicant = normalizeApplicantSponsorIds({
    ...applicantDoc.applicant,
    name: applicantDoc.applicant.name.trim(),
    surname: applicantDoc.applicant.surname.trim(),
  });
  if (applicantDoc.id) {
    const docRef = doc(db, "applicants", applicantDoc.id);
    applicantSnapshot = await getDoc(docRef);
    // Update the document
    if (applicantSnapshot) {
      applicantDoc.applicant.updatedAt = new Date().toISOString();
      applicantDoc.applicant.updatedBy = user ? user.id : "anonymous";
      await setDoc(docRef, applicantDoc.applicant);
      console.log("updated applicant:", applicantDoc.id);
    } else {
      console.log(`No document found with id: ${applicantDoc.id}`);
    }
  } else if (addIfNew) {
    console.log("adding new applicant");
    applicantDoc.applicant.createdAt = new Date().toISOString();
    applicantDoc.applicant.createdBy = user ? user.id : "anonymous";
    applicantDoc.id = (await addDoc(collection(db, "applicants"), applicantDoc.applicant)).id;
  } else {
    return null;
  }

  return applicantDoc.id;
};

export const addSponsorToApplicant = async (
  applicantId: string,
  sponsorId: string,
  user?: AppUser | null,
) => {
  if (!applicantId || !sponsorId) {
    return;
  }

  const updatedAt = new Date().toISOString();
  await updateDoc(doc(db, "applicants", applicantId), {
    sponsorIds: arrayUnion(sponsorId),
    updatedAt,
    updatedBy: user?.id || "anonymous",
  });
};

export const deleteApplicant = async (applicantId: string) => {
  if (!applicantId) return;

  return deleteDoc(doc(db, "applicants", applicantId));
};

export const findApplicantByIdAndEmail = async (applicantId: string, email: string) => {
  //TODO: excahnge this with a server function

  if (!applicantId || !email) {
    return null;
  }

  // Create a query to find the applicant with the specified ID and email
  const applicant = await loadApplicant(applicantId);

  // Check if any documents were found
  if (applicant && applicant.applicant.email?.toLocaleLowerCase() === email.toLocaleLowerCase()) {
    return applicant;
  }

  return null;
};

export const generateApplicantEmailExtensionCode = async (
  user: AppUser | null | undefined,
  applicantId: string
) => {
  if (!user || !applicantId) {
    return null;
  }

  const applicantDoc = await loadApplicant(applicantId);
  if (!applicantDoc) {
    return null;
  }

  const existingCode = applicantDoc.applicant.email_extension_code?.trim();
  if (existingCode) {
    return applicantDoc;
  }

  for (let attempt = 0; attempt < MAX_GENERATION_ATTEMPTS; attempt += 1) {
    const code = createRandomEmailExtensionCode();
    if (await isEmailExtensionCodeInUse(code, applicantId)) {
      continue;
    }

    const updatedApplicantDoc = {
      ...applicantDoc,
      applicant: {
        ...applicantDoc.applicant,
        email_extension_code: code,
      },
    };

    await storeApplicant(user, updatedApplicantDoc, false);
    return updatedApplicantDoc;
  }

  throw new Error(`Unable to generate a unique email extension code for applicant ${applicantId}`);
};

type SyncApplicantEmailCodesResponse = {
  status: "OK" | "PARTIAL" | "FAILED";
  message: string;
  summary?: {
    totalApplicants: number;
    synced: number;
    deleted: number;
    skippedMissingCode: number;
    failed: number;
  };
  failedDocuments?: string[];
};

export const syncApplicantEmailCodes = async (
  currentUser: AuthUser | null | undefined
): Promise<SyncApplicantEmailCodesResponse> => {
  const authToken = await currentUser?.authUser?.getIdToken();

  if (!authToken) {
    throw new Error("You must be signed in to synchronize email codes.");
  }

  const response = await fetch(`${BASE_FUNCTIONS_URL}/v1/applicants/sync-email-extension-codes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });

  const responseText = await response.text();
  let payload: SyncApplicantEmailCodesResponse = {
    status: "FAILED",
    message: "Failed to synchronize email codes.",
  };

  if (responseText) {
    try {
      payload = JSON.parse(responseText) as SyncApplicantEmailCodesResponse;
    } catch {
      payload = {
        status: "FAILED",
        message: responseText,
      };
    }
  }

  if (!response.ok) {
    throw new Error(payload.message || responseText || "Failed to synchronize email codes.");
  }

  return payload;
};
